// i18n entry point. Resolves the active locale (customer override →
// browser preference → 'en' fallback) and serves the matching message
// dictionary. Bundle cost: all 7 dictionaries are inlined statically so
// there's no runtime fetch / dynamic import — cheap because each
// dictionary is ~1 KB unminified, and tree-shaking still drops them
// from a host page that never opens the widget panel.

import { de } from './de.ts';
import { en } from './en.ts';
import { es } from './es.ts';
import { fr } from './fr.ts';
import { it } from './it.ts';
import { nl } from './nl.ts';
import { pt } from './pt.ts';
import { SUPPORTED_LOCALES, type SupportedLocale, type WidgetMessages } from './types.ts';

export type { SupportedLocale, WidgetMessages };
export { SUPPORTED_LOCALES };

const DICTIONARIES: Readonly<Record<SupportedLocale, WidgetMessages>> = {
  en,
  es,
  fr,
  de,
  it,
  pt,
  nl,
};

/**
 * Match a BCP-47 tag (or list) to one of our supported locales. Strips
 * regional subtags (`pt-BR` → `pt`), accepts the first acceptable language,
 * falls back to `en` if nothing matches.
 *
 * Customer override (from `/api/v1/widget-config`'s `locale` field) takes
 * precedence over the browser preference. We accept anything BCP-47-shaped
 * and only honor the primary subtag — if a Customer's per-workspace setting
 * says `de-AT`, we serve German.
 */
export function resolveLocale(
  override?: string | null,
  navigatorLanguages?: readonly string[],
): SupportedLocale {
  const candidates: string[] = [];
  if (override) candidates.push(override);
  if (navigatorLanguages) candidates.push(...navigatorLanguages);

  for (const tag of candidates) {
    const primary = tag.toLowerCase().split('-')[0];
    if (primary && (SUPPORTED_LOCALES as readonly string[]).includes(primary)) {
      return primary as SupportedLocale;
    }
  }
  return 'en';
}

/**
 * Convenience wrapper that reads `navigator.languages` (with fallback to
 * `navigator.language`) so callers can pass just the override.
 */
export function resolveLocaleFromBrowser(override?: string | null): SupportedLocale {
  if (typeof navigator === 'undefined') {
    return resolveLocale(override, []);
  }
  const languages: readonly string[] = navigator.languages?.length
    ? Array.from(navigator.languages)
    : navigator.language
      ? [navigator.language]
      : [];
  return resolveLocale(override, languages);
}

export function getMessages(locale: SupportedLocale): WidgetMessages {
  return DICTIONARIES[locale];
}
